import { zipSync } from 'fflate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildMidi, midiFileName } from '../core/midiwriter';
import { decodeSettings, DEFAULT_SETTINGS, type Settings } from '../core/settings';
import { buildDemoTune } from '../demo/demoTune';
import { PreviewPlayer } from '../preview/synth';
import { AppBar } from './components/AppBar';
import { ControlsPanel } from './components/ControlsPanel';
import { DocDialog, WelcomeDialog } from './components/Dialogs';
import { FileDrop } from './components/FileDrop';
import { FileInfo, SubtuneSelector } from './components/FileInfo';
import { PianoRoll } from './components/PianoRoll';
import { Player } from './components/Player';
import { RegisterLog } from './components/RegisterLog';
import type { DocId } from './docs';
import { readBool, WELCOME_KEY } from './prefs';
import { applyTheme, getTheme, setTheme as saveTheme, type ThemeId } from './theme';
import { useConverter } from './useConverter';
import { download } from './util';

const DEBOUNCE_MS = 150;

function initialSettings(): Settings {
  const enc = new URLSearchParams(window.location.search).get('s');
  return (enc && decodeSettings(enc)) || DEFAULT_SETTINGS;
}

type Dialog = { kind: 'welcome' } | { kind: 'doc'; doc: DocId } | null;

export default function App() {
  const conv = useConverter();
  const [settings, setSettings] = useState<Settings>(initialSettings);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [theme, setThemeState] = useState<ThemeId>(getTheme);
  const [dialog, setDialog] = useState<Dialog>(() =>
    readBool(WELCOME_KEY, true) ? { kind: 'welcome' } : null,
  );
  const [zoom, setZoom] = useState(40);
  const [showRaw, setShowRaw] = useState(false);
  const [view, setView] = useState<'roll' | 'registers'>('roll');
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [position, setPosition] = useState(0);
  const [mutes, setMutes] = useState<boolean[]>([]);
  const [solos, setSolos] = useState<boolean[]>([]);
  const playerRef = useRef<PreviewPlayer | null>(null);
  const pendingSelect = useRef<string | null>(null);

  const player = useMemo(() => {
    if (!playerRef.current) playerRef.current = new PreviewPlayer();
    return playerRef.current;
  }, []);

  useEffect(() => applyTheme(theme), [theme]);
  const changeTheme = (t: ThemeId) => {
    saveTheme(t);
    setThemeState(t);
  };

  const activeFile = conv.files.find((f) => f.id === activeId) ?? null;
  const header = activeFile?.header ?? null;
  const result = conv.result && conv.result.fileId === activeId ? conv.result : null;
  const song = result?.interpretation ?? null;

  // Select a newly added file as soon as it has loaded.
  useEffect(() => {
    const id = pendingSelect.current;
    if (!id) return;
    const f = conv.files.find((x) => x.id === id);
    if (f && (f.header || f.error)) {
      pendingSelect.current = null;
      setActiveId(id);
      setSettings((s) => ({ ...s, subtune: 0 }));
    }
  }, [conv.files]);

  // Re-run conversion (debounced) whenever the file or settings change.
  const { convert } = conv;
  useEffect(() => {
    if (!activeId || !header) return;
    const t = window.setTimeout(() => convert(activeId, settings), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [activeId, header, settings, convert]);

  // Feed new results into the preview player.
  useEffect(() => {
    if (!song) return;
    player.load(song);
    setMutes((m) => (m.length === song.tracks.length ? m : song.tracks.map(() => false)));
    setSolos((s) => (s.length === song.tracks.length ? s : song.tracks.map(() => false)));
  }, [song, player]);

  useEffect(() => {
    player.setTrackState(mutes, solos);
  }, [mutes, solos, player]);

  useEffect(() => {
    player.loop = loop;
  }, [loop, player]);

  useEffect(() => {
    player.onEnded = () => setPlaying(false);
    return () => player.dispose();
  }, [player]);

  // Position display while playing.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      setPosition(player.position);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, player]);

  const play = useCallback(() => {
    if (!song) return;
    void player.play(position >= song.duration ? 0 : position);
    setPlaying(true);
  }, [player, position, song]);

  const stop = useCallback(() => {
    player.stop();
    setPlaying(false);
    setPosition(player.position);
  }, [player]);

  // Stop playback when switching files.
  useEffect(() => {
    player.stop();
    setPlaying(false);
    setPosition(0);
  }, [activeId, player]);

  // Keyboard shortcuts: space = play/stop, +/- = zoom, ? = manual.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (dialog) return;
      const el = e.target as HTMLElement;
      const tag = el.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el.isContentEditable ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      )
        return;
      if (e.key === ' ' && tag !== 'BUTTON' && el.getAttribute('role') !== 'button') {
        e.preventDefault();
        if (playing) stop();
        else play();
      } else if (e.key === '+' || e.key === '=') {
        setZoom((z) => Math.min(400, z * 1.25));
      } else if (e.key === '-' || e.key === '_') {
        setZoom((z) => Math.max(5, z / 1.25));
      } else if (e.key === '?') {
        setDialog({ kind: 'doc', doc: 'manual' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [play, stop, playing, dialog]);

  const addFiles = async (items: { name: string; bytes: ArrayBuffer }[]) => {
    const ids = await conv.addFiles(items);
    if (ids.length) pendingSelect.current = ids[0];
  };

  const loadDemo = () => {
    const bytes = buildDemoTune();
    void addFiles([{ name: 'sid2midi-demo.sid', bytes: bytes.buffer.slice(0) as ArrayBuffer }]);
  };

  const subtune = header ? settings.subtune || header.startSong : 1;
  const meta = header ? { title: header.name, author: header.author, released: header.released } : null;

  const downloadMidi = () => {
    if (!song || !meta) return;
    download(midiFileName(meta, subtune), buildMidi(song, meta, { ppq: settings.ppq }), 'audio/midi');
  };

  const downloadZip = () => {
    if (!song || !meta) return;
    const files: Record<string, Uint8Array> = {};
    song.tracks.forEach((t, i) => {
      const label =
        t.kind === 'drums' ? 'drums' : t.kind === 'merged' ? 'voices' : `voice ${(t.voice ?? 0) + 1}`;
      files[midiFileName(meta, subtune, ` - ${label}`)] = buildMidi(song, meta, {
        ppq: settings.ppq,
        onlyTracks: [i],
      });
    });
    files[midiFileName(meta, subtune)] = buildMidi(song, meta, { ppq: settings.ppq });
    download(
      midiFileName(meta, subtune).replace(/\.mid$/, '.zip'),
      zipSync(files, { level: 6 }),
      'application/zip',
    );
  };

  const audible = useMemo(() => {
    const n = song?.tracks.length ?? 0;
    const anySolo = solos.some(Boolean);
    return Array.from({ length: n }, (_, i) => (anySolo ? !!solos[i] : !mutes[i]));
  }, [song, mutes, solos]);

  const warnings = result?.capture.warnings ?? [];
  const closeDialog = useCallback(() => setDialog(null), []);

  return (
    <div className="flex min-h-screen flex-col">
      <AppBar
        theme={theme}
        onTheme={changeTheme}
        onHelp={() => setDialog({ kind: 'doc', doc: 'manual' })}
        onAbout={() => setDialog({ kind: 'welcome' })}
      />

      <div className="grid flex-1 grid-cols-1 gap-2 p-2 md:grid-cols-[17rem_1fr] xl:grid-cols-[17rem_1fr_21rem]">
        <aside className="panel flex flex-col gap-4 p-3" aria-label="Input">
          <FileDrop
            files={conv.files}
            activeId={activeId}
            onFiles={(items) => void addFiles(items)}
            onSelect={(id) => {
              setActiveId(id);
              setSettings((s) => ({ ...s, subtune: 0 }));
            }}
            onRemove={(id) => {
              conv.removeFile(id);
              if (id === activeId) setActiveId(null);
            }}
            onDemo={loadDemo}
          />
          {activeFile?.error && (
            <p role="alert" className="notice-err text-[12px]">
              {activeFile.error}
            </p>
          )}
          {header && (
            <>
              <SubtuneSelector
                header={header}
                value={settings.subtune}
                onChange={(v) => setSettings((s) => ({ ...s, subtune: v }))}
              />
              <FileInfo header={header} capture={result?.capture ?? null} />
            </>
          )}
          <div className="mt-auto space-y-2 text-[11px] text-muted">
            <p>All processing happens in your browser. Your SID files never leave your computer.</p>
            <p>
              Converted arrangements of other people’s music remain subject to the original composer’s rights.
              Find tunes in the{' '}
              <a
                className="underline hover:text-accent"
                href="https://www.hvsc.c64.org/"
                target="_blank"
                rel="noreferrer"
              >
                High Voltage SID Collection
              </a>
              .
            </p>
          </div>
        </aside>

        <main className="panel flex min-w-0 flex-col gap-2 p-3" aria-label="Visualisation">
          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" aria-label="View" className="seg">
              {(['roll', 'registers'] as const).map((v) => (
                <button
                  key={v}
                  role="tab"
                  type="button"
                  aria-selected={view === v}
                  onClick={() => setView(v)}
                  className="seg-btn"
                >
                  {v === 'roll' ? 'Piano roll' : 'Register log'}
                </button>
              ))}
            </div>
            {view === 'roll' && (
              <>
                <button
                  type="button"
                  className="btn px-2"
                  aria-label="Zoom out"
                  aria-keyshortcuts="-"
                  onClick={() => setZoom((z) => Math.max(5, z / 1.25))}
                >
                  −
                </button>
                <button
                  type="button"
                  className="btn px-2"
                  aria-label="Zoom in"
                  aria-keyshortcuts="+"
                  onClick={() => setZoom((z) => Math.min(400, z * 1.25))}
                >
                  +
                </button>
                <label className="flex items-center gap-1.5 text-[12px] text-muted">
                  <input type="checkbox" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} />
                  Show raw frequency trace
                </label>
              </>
            )}
            <span role="status" aria-live="polite" className="ml-auto font-mono text-[11px] text-muted">
              {conv.busy === 'capturing'
                ? 'Emulating the C64…'
                : conv.busy === 'interpreting'
                  ? 'Updating…'
                  : result
                    ? `${song!.stats.notes} notes · ${song!.stats.arpRegions} arpeggios · ${song!.stats.drumHits} drum hits · ${
                        result.timings.cached
                          ? ''
                          : `captured in ${Math.round(result.timings.captureMs)} ms · `
                      }interpreted in ${Math.round(result.timings.interpretMs)} ms`
                    : ''}
            </span>
          </div>

          {conv.error && activeId && (
            <p role="alert" className="notice-err">
              {conv.error}
            </p>
          )}
          {warnings.map((w) => (
            <p key={w} className="notice-warn text-[12px]">
              {w}
            </p>
          ))}

          {!activeId && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
              <p className="legend text-[15px]">Drop a .sid file to get started</p>
              <p className="max-w-md text-muted">
                SID2MIDI emulates the C64, records what the music player writes to the SID chip and turns it
                into a MIDI file with one track per voice, a drum track and controller lanes for filter and
                pulse width.
              </p>
              <div className="flex gap-2">
                <button type="button" className="btn btn-primary btn-lg" onClick={loadDemo}>
                  Try the demo tune
                </button>
                <button
                  type="button"
                  className="btn btn-lg"
                  onClick={() => setDialog({ kind: 'doc', doc: 'manual' })}
                >
                  User Manual
                </button>
              </div>
            </div>
          )}

          {song && view === 'roll' && (
            <PianoRoll
              song={song}
              position={position}
              playing={playing}
              zoom={zoom}
              showRaw={showRaw}
              audible={audible}
              theme={theme}
              onSeek={(t) => {
                player.seek(t);
                setPosition(t);
              }}
            />
          )}
          {song && view === 'roll' && (
            <p className="text-[11px] text-muted">
              Click to move the playhead · outlined notes are merged arpeggio chords · diamonds are drum hits
            </p>
          )}
          {result && activeId && view === 'registers' && (
            <RegisterLog
              fileId={activeId}
              version={result}
              tickRate={result.capture.tickRate}
              position={position}
              request={conv.requestWrites}
            />
          )}
        </main>

        <aside
          className="panel overflow-y-auto md:col-span-2 xl:col-span-1 xl:max-h-[calc(100vh-7.5rem)]"
          aria-label="Conversion settings"
        >
          <ControlsPanel settings={settings} onChange={setSettings} />
        </aside>
      </div>

      <footer className="sticky bottom-0 border-t border-line bg-panel">
        <Player
          song={song}
          playing={playing}
          loop={loop}
          position={position}
          mutes={mutes}
          solos={solos}
          onPlay={play}
          onStop={stop}
          onLoop={setLoop}
          onMute={(i) => setMutes((m) => m.map((v, j) => (j === i ? !v : v)))}
          onSolo={(i) => setSolos((s) => s.map((v, j) => (j === i ? !v : v)))}
          onDownload={downloadMidi}
          onDownloadZip={downloadZip}
        />
      </footer>

      {dialog?.kind === 'welcome' && (
        <WelcomeDialog onClose={closeDialog} onOpenDoc={(doc) => setDialog({ kind: 'doc', doc })} />
      )}
      {dialog?.kind === 'doc' && <DocDialog doc={dialog.doc} onClose={closeDialog} />}
    </div>
  );
}
